// browse-bin — composer slot-layout (DECISION-052 convergence).
//
// A linear comic lane (like linear-spine-row) but lane dimensions come from the manifest
// slot VOLUME (w×h×d), NOT anchor.scale — socket-driven fixtures author the anchor at
// scale-1, with the lane expressed as the socket/slot volume. Used by the longbox LANE
// socket (spine-down comics, browsed front-to-back; covers face the customer via frontDir).
//
// Output is identical in shape to linear-spine-row (slots[] + extras{laneCenter, laneSize,
// slotStepVector, slotQuaternion, heroExtract, maxSlots, slotCount}) so the composer's
// populateComics + open-close/browse-1d/hero-pickup consume it unchanged.

import * as THREE from "../../../../vendor/three.module.js";
import { boxedComicReferenceMeters } from "../../../authoring/contracts.js";
import { registerSlotLayout } from "./index.js";

function computeBrowseBin({ anchor, manifest, slotDef, variant, countOverride, frontDir, productCount }) {
	const params = (variant?.params) || {};
	const legacy = slotDef || (Array.isArray(manifest?.slots) ? manifest.slots[0] : null) || {};
	const pick = (key, dflt) => (typeof params[key] === "number" ? params[key] : (typeof legacy[key] === "number" ? legacy[key] : dflt));
	const vol = variant?.volume || legacy.volume || {};

	const COMIC_THICKNESS = boxedComicReferenceMeters.thickness;
	const COMIC_HEIGHT    = boxedComicReferenceMeters.height;
	const spacingMult = pick("spacingMultiplier",   1.2);
	const marginMult  = pick("marginMultiplier",    0.7);
	const extractDist = pick("extractDistance",     0.18);
	const extractHF   = pick("extractHeightFactor", 0.85);
	const slotSpacing = Math.max(COMIC_THICKNESS * spacingMult, 0.006);
	const slotMargin  = Math.max(COMIC_THICKNESS * marginMult,  0.016);

	// VOLUME-driven lane (the key difference from linear-spine-row); fall back to anchor.scale.
	const laneLength = Math.max(0.12, vol.w ?? anchor.scale?.x ?? 0.12);
	const laneHeight = Math.max(0.08, vol.h ?? anchor.scale?.y ?? 0.08);
	const laneDepth  = Math.max(0.08, vol.d ?? anchor.scale?.z ?? 0.08);

	const usable  = Math.max(0, laneLength - slotMargin * 2);
	const geomCap = Math.max(1, Math.floor(usable / slotSpacing) + 1);   // physical cap from lane length
	const declared = countOverride ?? (typeof variant?.count?.default === "number" ? variant.count.default
		: typeof variant?.count?.fixed === "number" ? variant.count.fixed
		: typeof legacy.count === "number" ? legacy.count : null);
	let maxSlots = (typeof declared === "number" && declared > 0) ? Math.max(1, declared) : geomCap;
	maxSlots = Math.min(maxSlots, geomCap);                              // never overflow the lane
	const slotCount = Math.min(maxSlots, Math.max(0, productCount));

	const laneDir = frontDir.clone();
	const slotOneOffset  = laneLength * 0.5 - slotMargin;
	const slotStepVector = laneDir.clone().multiplyScalar(-slotSpacing);
	const slotOne        = anchor.position.clone().addScaledVector(laneDir, slotOneOffset);

	const upWorld = new THREE.Vector3(0, 1, 0);
	const xAxis   = new THREE.Vector3().crossVectors(upWorld, frontDir).normalize();
	const slotQuaternion = new THREE.Quaternion().setFromRotationMatrix(
		new THREE.Matrix4().makeBasis(xAxis, upWorld, frontDir),
	);

	const slots = [];
	for (let i = 0; i < slotCount; i++) {
		slots.push({
			position:   slotOne.clone().add(slotStepVector.clone().multiplyScalar(i)),
			quaternion: slotQuaternion.clone(),
		});
	}

	const laneCenter = anchor.position.clone();
	const heroExtract = laneCenter.clone()
		.addScaledVector(laneDir, laneDepth * 0.5 + extractDist)
		.add(new THREE.Vector3(0, COMIC_HEIGHT * extractHF, 0));

	return {
		slots,
		extras: { laneCenter, laneSize: new THREE.Vector3(laneLength, laneHeight, laneDepth), slotStepVector, slotQuaternion, heroExtract, maxSlots, slotCount },
	};
}

registerSlotLayout("browse-bin", computeBrowseBin);
export { computeBrowseBin };
